// public/js/features/display/settings-display.js
// "Display" settings tab — the entry point to the configurable display
// templates (docs/asset-display-templates.md).
//
// One card per surface, so the tab answers the two questions a user actually
// has at a glance: what can I change, and am I currently following my tenant or
// my own copy. Editing happens in the modal from template-editor.js.
//
// Tenant coupling: like every tenant-bound settings tab, the GLOBAL sidebar
// scope is the only tenant choice in the app. Under "All tenants" the tab just
// asks for a scope instead of showing a second picker — a template belongs to
// exactly one tenant, so editing it without a scope would be ambiguous.
// A user with a single tenant is pinned to it automatically.

import { escapeHtml } from '../../utils/escape.js';
import { scopeTenantParam, getMemberships } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';
import { getCurrentUser, can } from '../../modules/rbac.js';
import {
  SURFACE_LIST, SURFACE_META,
  loadTemplates, hasUserOverride, invalidateTemplates,
} from './template-store.js';
import { openTemplateEditor } from './template-editor.js';
import { injectDisplayStyles } from './display.css.js';

/**
 * Render the Display settings tab into `container`.
 * Open to every authenticated user: personal overrides are a per-user
 * preference. Only `manageTenants` sees the "Save for everyone" action.
 */
export async function renderDisplaySettings(container) {
  injectDisplayStyles();
  container.innerHTML = `<div class="text-muted small p-2"><i class="fa-solid fa-spinner fa-spin me-1"></i> Loading…</div>`;

  // Memberships (not loadTenants) — this tab is open to plain members too, and
  // they lack the listTenants permission.
  const memberships = getMemberships();
  const scopeTid = scopeTenantParam();
  const pinned = scopeTid && scopeTid !== 'unassigned' && memberships.some(t => t.tenantId === scopeTid);

  if (!pinned) {
    if (memberships.length === 1) {
      // Single tenant, scope not yet resolved to it — treat it as pinned.
      await draw(container, memberships[0].tenantId, memberships[0].name);
      return;
    }
    container.innerHTML = scopePromptHtml('display templates');
    return;
  }

  const name = memberships.find(t => t.tenantId === scopeTid)?.name || scopeTid;
  await draw(container, scopeTid, name);
}

async function draw(container, tenantId, tenantName) {
  const groups = getCurrentUser()?.groups || [];
  const canEditTenantDefault = can(groups, 'manageTenants');

  try {
    await loadTemplates(tenantId, { force: true });
  } catch {
    container.innerHTML = `<div class="alert alert-danger">Failed to load the display settings.</div>`;
    return;
  }

  container.innerHTML = `
    <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
      <h6 class="mb-0">What each view shows</h6>
      <span class="badge badge-soft-blue"><i class="fa-solid fa-building me-1"></i>${escapeHtml(tenantName || '')}</span>
    </div>
    <p class="text-muted small mb-3" style="max-width:720px;">
      Pick which asset fields appear in each view and in which order. Every view has a
      <strong>tenant default</strong> that applies to everyone${canEditTenantDefault ? ' (you may change it)' : ''},
      and you can keep a <strong>personal version</strong> for yourself. Without a personal version you always
      follow the current tenant default.
    </p>

    <div class="dt-surface-grid" id="dt-surfaces">
      ${SURFACE_LIST.map(surface => surfaceCardHtml(surface)).join('')}
    </div>`;

  container.querySelector('#dt-surfaces').addEventListener('click', (e) => {
    const btn = e.target.closest('.dt-edit');
    if (!btn) return;
    openTemplateEditor({
      surface: btn.dataset.surface,
      tenantId,
      canEditTenantDefault,
      onSaved: () => {
        // A save changes what every other page renders — drop the cache and
        // repaint the tab so the override badges are truthful straight away.
        invalidateTemplates();
        draw(container, tenantId, tenantName);
      },
    });
  });
}

function surfaceCardHtml(surface) {
  const meta = SURFACE_META[surface];
  const mine = hasUserOverride(surface);
  return `
    <div class="dt-surface-card">
      <div class="dt-surface-head">
        <span class="dt-surface-icon"><i class="fa-solid ${escapeHtml(meta.icon)}"></i></span>
        <span class="dt-surface-title">${escapeHtml(meta.title)}</span>
      </div>
      <div class="dt-surface-desc">${escapeHtml(meta.description)}</div>
      <div class="dt-surface-foot">
        ${mine
          ? `<span class="badge badge-soft-amber" title="You have your own version of this view — administrator changes do not reach it until you reset."><i class="fa-solid fa-user me-1"></i>Personal version</span>`
          : `<span class="badge badge-soft-gray" title="You follow the tenant default — administrator changes apply automatically."><i class="fa-solid fa-building me-1"></i>Tenant default</span>`}
        <button type="button" class="btn btn-sm btn-outline-primary dt-edit" data-surface="${escapeHtml(surface)}">
          <i class="fa-solid fa-pen me-1"></i>Edit
        </button>
      </div>
    </div>`;
}
